import * as THREE from "three";
import { MeshLine, MeshLineMaterial } from "meshline";
import { teethMeshObject } from "@/components/viewer/Mesh";
import { addEventListener, removeListenersFrom } from "./clickManager";
import {
  orthographicCamera,
  perspectiveCamera,
} from "@/components/viewer/Camera";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader";
import poppins from "@/static/poppins_regular.json";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry";
import {
  calculateIPRToothForNegation,
  getNextTooth,
  getPreviousTooth,
  getToothTotalIPR,
  moveToothDistally,
  moveToothMesially,
  setIsMultipleTeethMovement,
  setStopAutoSave,
  setToothMovementAmount,
  toothMovementAmount,
} from "@/components/viewer/teethControls/TeethPositionHelper";
import { customTeethGizmoControls } from "../components/viewer/Camera";

//#region 3D controls IPRs materials

const lineMaterial3DControls = new MeshLineMaterial({
  useMap: false, //tells the material to use map (0 - solid color, 1 use texture)
  color: "blue",
  opacity: 0.38,
  resolution: new THREE.Vector2(window.innerWidth, window.innerHeight), //THREE.Vector2 specifying the canvas size (REQUIRED)
  sizeAttenuation: false, // makes the line width constant regardless distance (1 unit is 1px on screen) (0 - attenuate, 1 - don't attenuate)
  transparent: true,
  lineWidth: 5,
  depthTest: true,
  alphaTest: 0, //cutoff value from 0 to 1
});

const circlePlusMaterial = new THREE.MeshBasicMaterial({
  color: new THREE.Color().convertSRGBToLinear(),
  map: new THREE.TextureLoader().load("IPRs/plus.png"),
  transparent: true,
  side: THREE.DoubleSide,
  toneMapped: false,
  polygonOffset: true,
  polygonOffsetFactor: -2,
});

const circleCloseMaterial = new THREE.MeshBasicMaterial({
  color: new THREE.Color().convertSRGBToLinear(),
  map: new THREE.TextureLoader().load("IPRs/close.png"),
  transparent: true,
  side: THREE.DoubleSide,
  toneMapped: false,
  polygonOffset: true,
  polygonOffsetFactor: -2,
});

const labelAddIPRMaterial = new THREE.MeshBasicMaterial({
  map: new THREE.TextureLoader().load("IPRs/Add IPR.png"),
  name: "Add IPR",
  side: THREE.DoubleSide,
  toneMapped: false,
  transparent: true,
  polygonOffset: true,
  polygonOffsetFactor: -4,
});

const labelAddSpacingMaterial = new THREE.MeshBasicMaterial({
  map: new THREE.TextureLoader().load("IPRs/Add Spacing.png"),
  name: "Add Spacing",
  side: THREE.DoubleSide,
  toneMapped: false,
  transparent: true,
  polygonOffset: true,
  polygonOffsetFactor: -4,
});

const whiteMaterial = new THREE.MeshBasicMaterial({
  color: 0xffffff,
  name: "white",
  side: THREE.DoubleSide,
  toneMapped: false,
});

const yellowMaterial = new THREE.MeshBasicMaterial({
  color: 0xebeba9,
  name: "yellow",
  side: THREE.DoubleSide,
  toneMapped: false,
});

const plusSquareMaterial = new THREE.MeshBasicMaterial({
  map: new THREE.TextureLoader().load("IPRs/plus box.png"),
  name: "increase",
  polygonOffset: true,
  polygonOffsetFactor: -20,
  side: THREE.DoubleSide,
  toneMapped: false,
  transparent: true,
});

const minusSquareMaterial = new THREE.MeshBasicMaterial({
  map: new THREE.TextureLoader().load("IPRs/minus box.png"),
  name: "decrease",
  polygonOffset: true,
  polygonOffsetFactor: -20,
  side: THREE.DoubleSide,
  toneMapped: false,
  transparent: true,
});

const binIconMaterial = new THREE.MeshBasicMaterial({
  color: 0x5d5d5d,
  map: new THREE.TextureLoader().load("IPRs/Bin.png"),
  name: "Bin",
  polygonOffset: true,
  polygonOffsetFactor: -2,
  side: THREE.DoubleSide,
  transparent: true,
  toneMapped: false,
});

//#endregion

//#region 3D controls variables
let options; // cached ref of toggle var in global store
let closeOptionsArray = []; // array has all the close options panel instances in the scene
let IPREditObjects = {}; // array has all the edit ipr or sapce instances in the scene
let lookAtCameraObjectsArray = []; // array that will make each object look at the camera on movement
let activeCameraIndex = 2;
let iprBefore3Dcontrols = {};

export function setActiveCameraIndex(index) {
  activeCameraIndex = index;
  objectsLookAtCamera();
}
let setLogs, setIprOverlaysState, setAutoSave, logs; // cached refs
export let iprOverLays = [];
let getContent3D;
const framesOutCount = 5; // 5 frames till the edit ipr or spacing rec closes on mouse out

const poppinsFont = new FontLoader().parse(poppins); // used font for all iprs text
//#endregion

//#region variables
let allIPRs = [];

export function addToIPRList(ipr) {
  allIPRs.push(ipr);
}

let lastStepArray;
//#endregion

//#region 3D controls IPRs

/**
 *
 * @param {Mesh} firstTooth the tooth that wanna add ipr to its right side
 * @action add Space the right side of the passed tooth
 */
function addIPR(firstTooth) {
  if (!firstTooth) {
    return;
  }
  let startTime = Date.now();
  const isLower = firstTooth.name.charAt(0) === "l";
  const secondTooth = isLower
    ? getPreviousTooth(firstTooth)
    : getNextTooth(firstTooth);

  calculateIPRToothForNegation(firstTooth, 1);
  calculateIPRToothForNegation(firstTooth, -1);

  calculateIPRToothForNegation(secondTooth, 1);
  calculateIPRToothForNegation(secondTooth, -1);

  const firstToothCenter = new THREE.Vector3();
  const secondToothCenter = new THREE.Vector3();

  let box3 = new THREE.Box3().setFromObject(firstTooth);
  box3.getCenter(firstToothCenter);

  box3 = new THREE.Box3().setFromObject(secondTooth);
  box3.getCenter(secondToothCenter);

  /*********************** movement cases */

  setIsMultipleTeethMovement(true);
  setStopAutoSave(true);
  if (firstToothCenter.x > 0) {
    moveToothDistally(
      firstTooth,
      logs,
      iprOverLays,
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  } else {
    moveToothMesially(
      firstTooth,
      logs,
      iprOverLays,
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  }
  setStopAutoSave(false);

  if (secondToothCenter.x <= 0) {
    moveToothDistally(
      secondTooth,
      logs,
      [],
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  } else {
    moveToothMesially(
      secondTooth,
      logs,
      [],
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  }
  setIsMultipleTeethMovement(false);
}

/**
 *
 * @param {Mesh} firstTooth the tooth that wanna add space to its right side
 * @action add Space the the right side of the passed tooth
 */
function addSpace(firstTooth) {
  if (!firstTooth) {
    return;
  }
  const isLower = firstTooth.name.charAt(0) === "l";
  const secondTooth = isLower
    ? getPreviousTooth(firstTooth)
    : getNextTooth(firstTooth);

  calculateIPRToothForNegation(firstTooth, 1);
  calculateIPRToothForNegation(firstTooth, -1);

  calculateIPRToothForNegation(secondTooth, 1);
  calculateIPRToothForNegation(secondTooth, -1);

  const firstToothCenter = new THREE.Vector3();
  const secondToothCenter = new THREE.Vector3();

  let box3 = new THREE.Box3().setFromObject(firstTooth);
  box3.getCenter(firstToothCenter);

  box3 = new THREE.Box3().setFromObject(secondTooth);
  box3.getCenter(secondToothCenter);

  /*********************** movement cases */

  setIsMultipleTeethMovement(true);
  setStopAutoSave(true);
  if (firstToothCenter.x > 0) {
    moveToothMesially(
      firstTooth,
      logs,
      iprOverLays,
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  } else {
    moveToothDistally(
      firstTooth,
      logs,
      iprOverLays,
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  }
  setStopAutoSave(false);

  if (secondToothCenter.x <= 0) {
    moveToothMesially(
      secondTooth,
      logs,
      [],
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  } else {
    moveToothDistally(
      secondTooth,
      logs,
      [],
      setLogs,
      setIprOverlaysState,
      setAutoSave,
    );
  }
  setIsMultipleTeethMovement(false);
}

/**
 *
 * @param {Mesh} firstTooth the tooth that wanna be deleting the IPRs from it's right side
 * @param {bool} isIPR if IPR then true else false
 * @action delete IPR/Spacing from the right side of the passed tooth
 */
function deleteIPROrSpacing(firstTooth, isIPR) {
  if (!firstTooth) {
    return;
  }

  const jawIndex = firstTooth.name.charAt(0) === "l" ? 1 : 0;
  const totalIPR = Math.abs(
    getToothTotalIPR(
      jawIndex,
      firstTooth.name.split("_")[2],
      iprOverLays,
      false,
    ),
  );
  const oldToothMovementAmount = toothMovementAmount;
  setToothMovementAmount(totalIPR / 2);

  if (isIPR) {
    addSpace(firstTooth);
  } else {
    addIPR(firstTooth);
  }
  setToothMovementAmount(oldToothMovementAmount);
}

export function setShow3DControls(state = false) {
  options.show3DControls = state;
}

export function setIprOverlays(_iprOverlays) {
  iprOverLays = _iprOverlays;
}

/**
 * @action Values should be filled before start executing the ipr 3D controls functions
 */
export function set3DControlsVariables(
  _logs,
  _iprOverlays,
  _setLogs,
  _setIprOverlays,
  _setAutoSave,
  _getContent,
) {
  getContent3D = _getContent;
  logs = _logs;
  iprOverLays = _iprOverlays;
  setLogs = _setLogs;
  setIprOverlaysState = _setIprOverlays;
  setAutoSave = _setAutoSave;
}

/**
 * @action Makes all objects in the {lookAtCameraObjectsArray} array look at the camera
 */
export function objectsLookAtCamera() {
  if (options.show3DControls) {
    for (let index = 0; index < lookAtCameraObjectsArray.length; index++) {
      lookAtCamera(lookAtCameraObjectsArray[index]);
    }
  }
}

const geometryPlusCashed = new THREE.CircleGeometry(2, 24);
const plusSignName = "plus sign BTN";
/**
 *
 * @param {Object} oldSprite if passed the object will be replaced with the result of the function as in hierarichy
 * @param {Vector3} position local position
 * @returns circle Mesh with dimentions of (2,24)
 */
function createPlusSign(oldSprite, position) {
  const circle = new THREE.Mesh(geometryPlusCashed, circlePlusMaterial.clone());
  circle.name = plusSignName;
  circle.position.copy(position);

  if (oldSprite) {
    oldSprite.parent.add(circle);
    oldSprite.scale.set(0, 0, 0);

    removeListenersFrom(oldSprite);
    oldSprite.removeFromParent();

    lookAtCameraObjectsArray.splice(
      lookAtCameraObjectsArray.indexOf(oldSprite),
      1,
    );
  }
  addEventListener(circle, "mousedown", () => {
    if (circle.parent) {
      circle.parent.add(createCloseOptions(circle));
    }

    setTimeout(() => {
      removeListenersFrom(circle);
    }, 0.01);

    circle.scale.set(0, 0, 0);

    circle.removeFromParent();
    removeListenersFrom(circle);

    lookAtCameraObjectsArray.splice(
      lookAtCameraObjectsArray.indexOf(circle),
      1,
    );
  });

  addEventListener(null, "mousemove", info => {
    if (!circle.parent || !circle.parent.parent) {
      // Auto clear listener if not in use any more

      removeListenersFrom(info);
    }
  });
  lookAtCameraObjectsArray.push(circle);

  return circle;
}

const geometryCloseCashed = new THREE.CircleGeometry(2, 24);
/**
 *
 * @returns circle Mesh with dimentions of (3,24)
 */
function createCloseSign() {
  const circle = new THREE.Mesh(
    geometryCloseCashed,
    circleCloseMaterial.clone(),
  );
  circle.name = "close sign BTN";
  return circle;
}

/**
 *
 * @param {Material} material
 * @returns Mesh of a plane with the assgined material
 */
function createRectangleSign(material) {
  const geometry = new THREE.PlaneGeometry(12, 3);
  const rectangle = new THREE.Mesh(geometry, material);
  rectangle.name = material.name;
  return rectangle;
}

/**
 *
 * @param {Mesh} object has to be a grand child of a tooth
 * @return close BTN with 2 childs => add IPR & spacing rects BTNs as meshs
 */
function createCloseOptions(object) {
  const isLower = object.name.charAt(0) === "l";
  const newLabel = createCloseSign();
  newLabel.position.copy(object.position);

  const addIPRRect = createRectangleSign(labelAddIPRMaterial);
  const addSpacingRect = createRectangleSign(labelAddSpacingMaterial);

  addIPRRect.position.y += isLower ? -4 : 4;
  addSpacingRect.position.y += isLower ? -7 : 7;

  const IPRTextGeometry = new TextGeometry(getContent3D("actions", "add_ipr"), {
    font: poppinsFont,
    size: 0.8,
    depth: 0.1,
  });

  const spacingTextGeometry = new TextGeometry(
    getContent3D("actions", "add_space"),
    {
      font: poppinsFont,
      size: 0.8,
      depth: 0.1,
    },
  );

  IPRTextGeometry.computeBoundingBox();
  const IPRTextOBJ = new THREE.Mesh(
    IPRTextGeometry,
    new THREE.MeshPhongMaterial({
      color: 0x000000,
      flatShading: true,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetUnits: 1,
      polygonOffsetFactor: -15,
    }),
  );

  spacingTextGeometry.computeBoundingBox();
  const spaceTextOBJ = new THREE.Mesh(
    spacingTextGeometry,
    new THREE.MeshPhongMaterial({
      color: 0x000000,
      flatShading: true,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetUnits: 1,
      polygonOffsetFactor: -15,
    }),
  );

  IPRTextOBJ.position.set(-2.2, -0.1, 0);
  spaceTextOBJ.position.set(-2.2, -0.1, 0);

  addIPRRect.add(IPRTextOBJ);
  addSpacingRect.add(spaceTextOBJ);

  newLabel.add(addIPRRect);
  newLabel.add(addSpacingRect);

  lookAtCamera(newLabel);

  const line = object.parent;
  addEventListener(addSpacingRect, "mousedown", info => {
    setToothMovementAmount(toothMovementAmount / 2);
    addSpace(line?.parent?.parent);
    setToothMovementAmount(toothMovementAmount * 2);
  });

  addEventListener(addIPRRect, "mousedown", info => {
    setToothMovementAmount(toothMovementAmount / 2);
    addIPR(line?.parent?.parent);
    setToothMovementAmount(toothMovementAmount * 2);
  });

  addEventListener(null, "mousemove", info => {
    if (!newLabel?.parent?.parent) {
      // Auto clear listener if not in use any more
      removeListenersFrom(newLabel.children[0]);
      removeListenersFrom(newLabel.children[1]);
      removeListenersFrom(info);
    }
  });

  lookAtCameraObjectsArray.push(newLabel);
  closeOptionsArray.push(newLabel);
  return newLabel;
}

/**
 *
 * @param {bool} isIPR if IPR then true else false
 * @returns a clickable mesh with 2 childs: 1- the bin Icon 2- text
 */
function createDeleteBTN(isIPR) {
  const bgGeometry = new THREE.PlaneGeometry(6, 1);
  const binGeometry = new THREE.PlaneGeometry(0.7, 1);

  const deleteBGRectangle = new THREE.Mesh(
    bgGeometry,
    isIPR ? whiteMaterial : yellowMaterial,
  );

  const binIcon = new THREE.Mesh(binGeometry, binIconMaterial);

  deleteBGRectangle.name = "delete BG Rectangle";

  const textGeometry = new TextGeometry(
    isIPR
      ? getContent3D("actions", "delete_ipr")
      : getContent3D("actions", "delete_space"),
    {
      font: poppinsFont,
      size: 0.8,
      depth: 0.1,
    },
  );

  textGeometry.computeBoundingBox();
  const textOBJ = new THREE.Mesh(
    textGeometry,
    new THREE.MeshPhongMaterial({ color: 0x000000, flatShading: true }),
  );
  binIcon.position.set(-3, 0.4, 0);
  textOBJ.position.set(-2.3, 0, 0);

  deleteBGRectangle.add(binIcon);
  deleteBGRectangle.add(textOBJ);

  return deleteBGRectangle;
}
const bgGeometryCached = new THREE.PlaneGeometry(13, 6);
const boxGeometryCached = new THREE.PlaneGeometry(3, 3);
const textMaterialCached = new THREE.MeshPhongMaterial({
  color: 0x000000,
  flatShading: true,
});
/**
 *
 * @param {bool} isIPR if IPR then true else false
 * @param {string} IPRValue the text that will be written
 * @param {Object} oldSprite if passed the object will be replaced with the result of the function as in hierarichy
 * @param {Vector3} position
 * @returns An hoverable object that holds 4 childrens, 1 plus btn, minus btn, text=> value of ipr, delete BTN
 */
function createEditingIPRSpacingRectangle(
  isIPR,
  IPRValue,
  oldSprite,
  position,
) {
  const bgRectangle = new THREE.Mesh(
    bgGeometryCached,
    isIPR ? whiteMaterial : yellowMaterial,
  );
  bgRectangle.name = "bg Editing rectangle";

  const increaseBoxRectangle = new THREE.Mesh(
    boxGeometryCached,
    plusSquareMaterial,
  );
  const decreaseBoxRectangle = new THREE.Mesh(
    boxGeometryCached,
    minusSquareMaterial,
  );

  const textGeometry = new TextGeometry(IPRValue, {
    font: poppinsFont,
    size: 0.8,
    depth: 0.1,
  });

  textGeometry.computeBoundingBox();
  const textOBJ = new THREE.Mesh(textGeometry, textMaterialCached);

  const deleteHolder = createDeleteBTN(isIPR);

  increaseBoxRectangle.name = "increase Box Rectangle";
  decreaseBoxRectangle.name = "decrease Box Rectangle";
  deleteHolder.name = "delete " + isIPR ? "IPR" : "Space";
  textOBJ.name = "IPR Value";

  increaseBoxRectangle.position.set(4.25, 1, 0);
  decreaseBoxRectangle.position.set(-4.25, 1, 0);
  textOBJ.position.set(-1, 0.5, 0);
  deleteHolder.position.set(0, -2, 0);

  bgRectangle.add(increaseBoxRectangle);
  bgRectangle.add(decreaseBoxRectangle);
  bgRectangle.add(deleteHolder);
  bgRectangle.add(textOBJ);

  bgRectangle.position.copy(position);
  /************************************* */

  addEventListener(deleteHolder, "mousedown", () => {
    deleteIPROrSpacing(deleteHolder?.parent?.parent?.parent?.parent, isIPR);
  });

  addEventListener(increaseBoxRectangle, "mousedown", info => {
    if (increaseBoxRectangle?.parent?.parent?.parent?.parent) {
      if (isIPR) {
        setToothMovementAmount(toothMovementAmount / 2);
        increaseBoxRectangle.parent.parent.parent.userData["remove"] = 2;
        addIPR(increaseBoxRectangle?.parent?.parent?.parent?.parent);
        setToothMovementAmount(toothMovementAmount * 2);
      } else {
        setToothMovementAmount(toothMovementAmount / 2);
        increaseBoxRectangle.parent.parent.parent.userData["remove"] = 2;
        addSpace(increaseBoxRectangle?.parent?.parent?.parent?.parent);
        setToothMovementAmount(toothMovementAmount * 2);
      }
      removeListenersFrom(bgRectangle);
      increaseBoxRectangle.scale.set(0, 0, 0);
    }
  });

  addEventListener(decreaseBoxRectangle, "mousedown", info => {
    if (increaseBoxRectangle?.parent?.parent?.parent?.parent) {
      if (isIPR) {
        setToothMovementAmount(toothMovementAmount / 2);
        increaseBoxRectangle.parent.parent.parent.userData["remove"] = 2;
        addSpace(increaseBoxRectangle?.parent?.parent?.parent?.parent);
        setToothMovementAmount(toothMovementAmount * 2);
      } else {
        setToothMovementAmount(toothMovementAmount / 2);
        increaseBoxRectangle.parent.parent.parent.userData["remove"] = 2;
        addIPR(increaseBoxRectangle?.parent?.parent?.parent?.parent);
        setToothMovementAmount(toothMovementAmount * 2);
      }
      removeListenersFrom(bgRectangle);
      increaseBoxRectangle.scale.set(0, 0, 0);
    }
  });

  addEventListener(null, "mousemove", info => {
    if (!bgRectangle?.parent?.parent) {
      // Auto clear listener if not in use any more
      removeListenersFrom(decreaseBoxRectangle);
      removeListenersFrom(increaseBoxRectangle);
      removeListenersFrom(deleteHolder);
      removeListenersFrom(info);
    }
  });
  lookAtCameraObjectsArray.push(bgRectangle);

  /*********************** */
  addEventListener(bgRectangle, "mouseover", info => {
    if (customTeethGizmoControls.dragging) {
      return;
    }
    IPREditObjects[bgRectangle.uuid].isOn = true;
  });
  addEventListener(bgRectangle, "mouseout", info => {
    IPREditObjects[bgRectangle.uuid].isOn = false;
  });

  oldSprite.parent.add(bgRectangle);
  removeListenersFrom(oldSprite);
  oldSprite.scale.set(0, 0, 0);
  oldSprite.visible = false;

  oldSprite.removeFromParent();
  removeListenersFrom(oldSprite);

  lookAtCameraObjectsArray.splice(
    lookAtCameraObjectsArray.indexOf(oldSprite),
    1,
  );

  IPREditObjects[bgRectangle.uuid] = {
    object: bgRectangle,
    isOn: false,
    isIPR,
    text: IPRValue,
    position,
    counter: 0,
  };

  return bgRectangle;
}

const margin = 0.05;
function ignoreValue(value) {
  // return value;
  return Math.abs(parseFloat(value)) > margin ? value : 0;
}

function createTextTexture(text, fontSize = 64, color = "#ffffff") {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = 512;
  canvas.height = 256;

  context.fillStyle = color;
  context.font = `${fontSize}px Arial`;
  context.textAlign = "center";
  context.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  return texture;
}

const bgGeometryCached2 = new THREE.PlaneGeometry(6, 3.2);

const geometryCached2 = new THREE.PlaneGeometry(4, 2);

const IPRSpacingtextOBJGeometryCached2 = new THREE.PlaneGeometry(4, 2);

/**
 *
 * @param {bool} isIPR if IPR then true else false
 * @param {string} value IPR value to be written
 * @param {Vector3} position
 * @returns Hoverable object with 2 children 1- IPR/Spacing Text, 2- IPR value
 */
function createIPRSpacingRectangle(isIPR, value, position) {
  const bgRectangle = new THREE.Mesh(
    bgGeometryCached2,
    isIPR ? whiteMaterial : yellowMaterial,
  );
  bgRectangle.name = "bg rectangle";

  const canvasText = createTextTexture(value, 160, 0x000000);
  const material = new THREE.MeshBasicMaterial({
    map: canvasText,
    transparent: true,
  });

  const textOBJ = new THREE.Mesh(geometryCached2, material);

  /* const textGeometry = new TextGeometry(value, {
    font: poppinsFont,
    size: 0.8,
    depth: 0.1,
  });

  textGeometry.computeBoundingBox();
  const textOBJ = new THREE.Mesh(
    textGeometry,
    new THREE.MeshPhongMaterial({ color: 0x000000, flatShading: true }),
  ); */

  /* const iprSpacingTextGeometry = new TextGeometry(
    isIPR ? getContent3D("labels", "ipr") : getContent3D("labels", "spacing"),
    {
      font: poppinsFont,
      size: 0.8,
      depth: 0.1,
    },
  );

  const IPRSpacingtextOBJ = new THREE.Mesh(
    iprSpacingTextGeometry,
    new THREE.MeshPhongMaterial({ color: 0x000000, flatShading: true }),
  ); */

  const IPRSpacingtextOBJCanvasText = createTextTexture(
    isIPR ? getContent3D("labels", "ipr") : getContent3D("labels", "spacing"),
    140,
    0x000000,
  );

  const IPRSpacingtextOBJMaterial = new THREE.MeshBasicMaterial({
    map: IPRSpacingtextOBJCanvasText,
    transparent: true,
  });
  const IPRSpacingtextOBJ = new THREE.Mesh(
    IPRSpacingtextOBJGeometryCached2,
    IPRSpacingtextOBJMaterial,
  );

  IPRSpacingtextOBJ.position.set(0, -1, 0.05);
  textOBJ.position.set(0, 0.5, 0.05);

  bgRectangle.add(textOBJ);
  bgRectangle.add(IPRSpacingtextOBJ);

  bgRectangle.position.copy(position);
  /**************************************************** */

  addEventListener(null, "mousemove", info => {
    if (!bgRectangle?.parent?.parent) {
      // Auto clear listener if not in use any more
      removeListenersFrom(bgRectangle);
      removeListenersFrom(info);
    }
  });
  lookAtCameraObjectsArray.push(bgRectangle);

  addEventListener(bgRectangle, "mouseover", info => {
    if (customTeethGizmoControls.dragging) {
      return;
    }
    const newLabel = createEditingIPRSpacingRectangle(
      isIPR,
      value,
      bgRectangle,
      bgRectangle.position,
    );

    lookAtCamera(newLabel);
  });

  return bgRectangle;
}
/**
 * @action deletes all CloseOptions panels in {closeOptionsArray} array
 */
export function deleteCloseOptions() {
  let label;

  for (let index = 0; index < closeOptionsArray.length; index++) {
    label = createPlusSign(
      closeOptionsArray[index],
      closeOptionsArray[index].position,
    );
    lookAtCamera(label);
  }
  closeOptionsArray = [];
}

/**
 * @action deletes iprOrSpacingEditing panels after {frames} mouse moves without being hovered at in {IPREditObjects}
 */
export function deleteEditingPanels() {
  const keys = Object.keys(IPREditObjects);

  for (let index = 0; index < keys.length; index++) {
    if (
      !IPREditObjects[keys[index]].isOn &&
      IPREditObjects[keys[index]].counter >= framesOutCount
    ) {
      const bgRectangle = IPREditObjects[keys[index]].object;
      const newLabel = createIPRSpacingRectangle(
        IPREditObjects[keys[index]].isIPR,
        IPREditObjects[keys[index]].text,
        IPREditObjects[keys[index]].position,
      );

      if (bgRectangle.parent) bgRectangle.parent.add(newLabel);
      removeListenersFrom(bgRectangle);
      bgRectangle.scale.set(0, 0, 0);
      bgRectangle.removeFromParent();

      lookAtCameraObjectsArray.splice(
        lookAtCameraObjectsArray.indexOf(bgRectangle),
        1,
      );
      lookAtCamera(newLabel);
      delete IPREditObjects[keys[index]];
    } else {
      IPREditObjects[keys[index]].counter++;
    }
  }
}

/**
 *
 * @param {Mesh} tooth A tooth
 * @param {Object} iprOverLays an IPROverLays Object shape of data
 * @action deletes the IPR object and redraw it as required
 */
export const updateIPRBetweenTeeth = (
  tooth,
  iprOverLays,
  isOriginal = false,
) => {
  let gumOffset = tooth.parent.parent.parent.position.z;
  const jawNamekey = tooth.name.charAt(0);
  const splittedName = tooth.name.split("_");
  const toothNum = splittedName[2];
  const isLower = jawNamekey === "u" ? 0 : 1;
  const iprs = iprOverLays[isLower][toothNum];
  let totalAmount = 0;
  iprs.forEach(ipr => {
    if (isNaN(ipr.amount)) {
      return;
    }
    if (ipr.amount === null) {
      return;
    }
    totalAmount += Number(ipr?.amount);
  });

  if (
    tooth.children[2].userData["remove"] > 0 &&
    ignoreValue(totalAmount) != 0
  ) {
    tooth.children[2].userData["remove"] =
      tooth.children[2].userData["remove"] - 1;
    if (tooth.children[2].userData["remove"] === 0) {
      const isSpacing = Number(Number(totalAmount)) < 0 ? true : false;
      totalAmount = Math.abs(totalAmount).toFixed(2);

      delete IPREditObjects[tooth.children[2].children[0].children[0].uuid];
      const holder = createEditingIPRSpacingRectangle(
        !isSpacing,
        totalAmount,
        tooth.children[2].children[0].children[0],
        tooth.children[2].children[0].children[0].position,
      );
      lookAtCamera(holder);
    }
  } else {
    if (jawNamekey === "u") {
      if (!getNextTooth(tooth)) {
        return;
      }
    } else {
      if (!getPreviousTooth(tooth)) {
        return;
      }
    }

    const step = Number(splittedName[1].substring(2, 5));
    if (iprs[step] === undefined) {
      console.log("exiting");
      return;
    }

    let linestart = new THREE.Vector3(
      iprs[step].ipr_position[0],
      iprs[step].ipr_position[1],
      iprs[step].ipr_position[2],
    );

    let lineEnd = getIPRPos(
      linestart.clone(),
      isLower === 0 ? 1 : -1,
      12,
      gumOffset,
    );

    const line = createLine(linestart, lineEnd, "#2b2f2e", true);
    line.visible = options.showLower;

    const isSpacing = Number(Number(totalAmount)) < 0 ? true : false;
    totalAmount = Math.abs(totalAmount).toFixed(2);

    iprs.forEach((ipr, i) => {
      if (
        ipr.amount === null ||
        isNaN(ipr.amount) ||
        Math.abs(Number(Number(totalAmount))) <= 0 ||
        Math.abs(Number(Number(ipr.amount).toFixed(2))) <= 0
      ) {
        return;
      }
    });

    /*************************************************************/

    line.visible = options.showLower;
    line.name = jawNamekey + "_line";
    let label;

    if (ignoreValue(totalAmount) > 0) {
      label = createIPRSpacingRectangle(
        !isSpacing,
        totalAmount,
        getSpritePosition(lineEnd),
      );
    } else {
      label = createPlusSign(null, getSpritePosition(lineEnd));
    }

    label.visible = options.showLower;
    lookAtCamera(label);

    line.add(label);
    if (isOriginal) {
      iprBefore3Dcontrols[tooth.name] = tooth.children[2].children[0]?.clone();
      if (iprBefore3Dcontrols[tooth.name]) {
        iprBefore3Dcontrols[tooth.name].name =
          iprBefore3Dcontrols[tooth.name]?.name + " test";
      }
    }
    const oldIPR = tooth.children[2].children[0];
    tooth.children[2].remove(tooth.children[2].children[0]);
    if (oldIPR?.children[0]) {
      removeListenersFrom(oldIPR.children[0]);
    }

    if (totalAmount) {
      tooth.children[2].add(line);
      allIPRs.push(line);
    }
  }
};

/**
 *
 * @param {Object} object
 * @action Makes the object look at Camera
 */
export function lookAtCamera(object) {
  if (!perspectiveCamera) {
    console.warn(object.name, " couldn't be assigned to follow the camera");
    return;
  }
  if (activeCameraIndex === 0) {
    object.quaternion.copy(orthographicCamera.quaternion);
  } else {
    object.quaternion.copy(perspectiveCamera.quaternion);
  }
}
//#endregion

//#region normal IPRs

export function reassignOriginalIPRs(_lastStepArray) {
  console.log(_lastStepArray);

  for (let jawIndex = 0; jawIndex < 2; jawIndex++) {
    _lastStepArray[jawIndex]?.children.forEach(tooth => {
      tooth.children[2].remove(tooth.children[2].children[0]);
      if (iprBefore3Dcontrols[tooth.name]) {
        tooth.children[2].add(iprBefore3Dcontrols[tooth.name]);
      }
    });
    _lastStepArray[jawIndex].update;
  }
}

function makeLabelCanvas(
  text,
  innerBgColor,
  bgColor,
  innerTextColor,
  outerTextColor,
  step,
  startRevision,
  isSpacing,
) {
  text = " " + text.toFixed(2) + " ";

  step = " " + step + " ";
  const radius = 20;
  const font = `bold 125px 'Arial'`;
  const outerCanvas = document.createElement("canvas");
  const outerContext = outerCanvas.getContext("2d");

  outerCanvas.width = startRevision ? 500 : 550;
  outerCanvas.height = startRevision ? 375 : 275;

  const innerCanvas = document.createElement("canvas");
  innerCanvas.width = startRevision ? 275 : 200;
  innerCanvas.height = 175;
  innerCanvas.style.marginRight = "10px"; // Set the right margin

  const innerContext = innerCanvas.getContext("2d");
  const innerRadius = 20;

  innerContext.beginPath();
  innerContext.moveTo(innerRadius, 0);
  innerContext.lineTo(innerCanvas.width - innerRadius, 0);
  innerContext.quadraticCurveTo(
    innerCanvas.width,
    0,
    innerCanvas.width,
    innerRadius,
  );
  innerContext.lineTo(innerCanvas.width, innerCanvas.height - innerRadius);
  innerContext.quadraticCurveTo(
    innerCanvas.width,
    innerCanvas.height,
    innerCanvas.width - innerRadius,
    innerCanvas.height,
  );
  innerContext.lineTo(innerRadius, innerCanvas.height);
  innerContext.quadraticCurveTo(
    0,
    innerCanvas.height,
    0,
    innerCanvas.height - innerRadius,
  );
  innerContext.lineTo(0, innerRadius);
  innerContext.quadraticCurveTo(0, 0, innerRadius, 0);
  innerContext.closePath();
  innerContext.fillStyle = startRevision
    ? isSpacing
      ? "#FFF1A8"
      : "#ffffff"
    : innerBgColor;
  innerContext.fill();
  innerContext.font = startRevision ? `500 100px 'Arial'` : font;
  innerContext.fillStyle = startRevision ? "#616060" : innerTextColor;
  innerContext.textAlign = "center";
  innerContext.textBaseline = "middle";
  innerContext.fillText(step, innerCanvas.width / 2, innerCanvas.height / 2);

  outerContext.shadowColor = "rgba(0, 0, 0, 0.25)";
  outerContext.shadowOffsetX = 0;
  outerContext.shadowOffsetY = 0;
  outerContext.shadowBlur = 50;

  outerContext.beginPath();
  outerContext.moveTo(radius, 0);
  outerContext.lineTo(outerCanvas.width - radius, 0);
  outerContext.quadraticCurveTo(
    outerCanvas.width,
    0,
    outerCanvas.width,
    radius,
  );
  outerContext.lineTo(outerCanvas.width, outerCanvas.height - radius - 15); // Subtract 10 here
  outerContext.quadraticCurveTo(
    outerCanvas.width,
    outerCanvas.height - 15, // Subtract 10 here
    outerCanvas.width - radius,
    outerCanvas.height - 15, // Subtract 10 here
  );
  outerContext.lineTo(radius, outerCanvas.height - 15); // Subtract 10 here
  outerContext.quadraticCurveTo(
    0,
    outerCanvas.height - 15, // Subtract 10 here
    0,
    outerCanvas.height - radius - 15, // Subtract 10 here
  );
  outerContext.lineTo(0, radius);
  outerContext.quadraticCurveTo(0, 0, radius, 0);
  outerContext.closePath();
  outerContext.fillStyle = startRevision
    ? isSpacing
      ? "#FFF1A8"
      : bgColor
    : bgColor;
  outerContext.fill();
  outerContext.font = font;

  // Reset the shadow values
  outerContext.shadowColor = "rgba(0,0,0,0)";
  outerContext.shadowOffsetX = 0;
  outerContext.shadowOffsetY = 0;
  outerContext.fillStyle = outerTextColor;
  outerContext.textAlign = "center";
  outerContext.textBaseline = "middle";
  outerContext.fillText(
    text,
    outerCanvas.width / (startRevision ? 2 : 4),
    outerCanvas.height / (startRevision ? 4 : 2),
  );
  // calculate the position of the innerCanvas
  const innerCanvasX =
    (outerCanvas.width - innerCanvas.width) / (startRevision ? 2 : 1.2);
  const innerCanvasY =
    (outerCanvas.width - innerCanvas.width) / (startRevision ? 1.5 : 8.5);

  outerContext.drawImage(innerCanvas, innerCanvasX, innerCanvasY);

  return outerContext.canvas;
}

function makeSpriteLabel(
  position,
  text,
  colors,
  step,
  i,
  startRevision,
  isSpacing,
) {
  let canvas = makeLabelCanvas(
    text,
    colors.innerBgColor,
    colors.bgColor,
    colors.innerTextColor,
    colors.outerTextColor,
    step,
    startRevision,
    isSpacing,
  );
  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;

  const labelMaterial = new THREE.SpriteMaterial({
    map: texture,
    side: THREE.DoubleSide,
    transparent: true,
    toneMapped: false,
    // depthTest: true,
    // polygonOffset: true,
    // polygonOffsetFactor: -200,
    // polygonOffsetUnits: 1,
  });

  const sprite = new THREE.Object3D();
  sprite.position.copy(position);
  sprite.position.z += 0.1;
  sprite.position.y += i * 3;
  const label = new THREE.Sprite(labelMaterial);

  label.renderOrder = 1;

  sprite.add(label);

  const labelBaseScale = 0.01;
  label.scale.x = canvas.width * labelBaseScale;
  label.scale.y = canvas.height * labelBaseScale;

  return sprite;
}

function getSpritePosition(position) {
  const newPosition = new THREE.Vector3();
  newPosition.copy(position);
  newPosition.z += 0.1;
  return newPosition;
}

const lineMaterial = new MeshLineMaterial({
  useMap: false, //tells the material to use map (0 - solid color, 1 use texture)
  color: "white",
  opacity: 0.38,
  resolution: new THREE.Vector2(window.innerWidth, window.innerHeight), //THREE.Vector2 specifying the canvas size (REQUIRED)
  sizeAttenuation: false, // makes the line width constant regardless distance (1 unit is 1px on screen) (0 - attenuate, 1 - don't attenuate)
  transparent: true,
  lineWidth: 5,
  depthTest: true,
  // polygonOffset: true,
  // polygonOffsetFactor: -20,
  // polygonOffsetUnits: 1,
  alphaTest: 0, //cutoff value from 0 to 1
});

let createLine = function (p0, p1, color, upper) {
  const samples = [p0, p1];

  const geometry = new THREE.BufferGeometry().setFromPoints(samples);
  const line = new MeshLine();
  line.setGeometry(geometry);

  const mesh = new THREE.Mesh(line.geometry, lineMaterial);
  mesh.name = upper ? "u_line" : "l_line";
  return mesh;
};
const tolerance = 1e-16;
function ignoreSmallNumber(number) {
  return Math.abs(number) < tolerance;
}
export const placeIPRBetweenTeeth = (gumOffset, _options, iprOverLays) => {
  options = _options;
  for (let isLower = 0; isLower < 2; isLower++) {
    const jawNamekey = isLower === 0 ? "u" : "l";
    Object.values(iprOverLays[isLower] || {}).forEach(iprs => {
      for (let step = 0; step < iprs.length; step++) {
        if (iprs[step] === undefined) return;

        let linestart = new THREE.Vector3(
          iprs[step].ipr_position[0],
          iprs[step].ipr_position[1],
          iprs[step].ipr_position[2],
        );

        let lineEnd = getIPRPos(
          linestart.clone(),
          isLower === 0 ? 1 : -1,
          12,
          gumOffset,
        );

        const line = createLine(linestart, lineEnd, "#2b2f2e", true);
        line.visible = options.showLower;
        let totalAmount = 0;

        iprs.forEach(ipr => {
          if (isNaN(ipr.amount)) {
            return;
          }
          if (ipr.amount === null) {
            return;
          }

          totalAmount += Number(ipr?.amount);
        });
        iprs.forEach((ipr, i) => {
          if (
            ipr.amount === null ||
            isNaN(ipr.amount) ||
            Math.abs(Number(Number(totalAmount).toFixed(2))) <= 0 ||
            Math.abs(Number(Number(ipr.amount).toFixed(2))) <= 0
          ) {
            return;
          }

          const amount = Math.abs(Number(ipr.amount).toFixed(2));
          let activeStep = Number(ipr?.step);

          let label = new THREE.Object3D();
          const isSpacing =
            Number(Number(totalAmount).toFixed(2)) < 0 ? true : false;
          label = makeSpriteLabel(
            lineEnd,
            /*numberingData.displayName*/
            amount,
            {
              innerBgColor: activeStep === step ? "#ffffff" : "#484848",
              bgColor: activeStep === step ? "#295DCD" : "#ffffff",
              innerTextColor: activeStep === step ? "#295DCD" : "#ffffff",
              outerTextColor: activeStep === step ? "#ffffff" : "#000000",
            },
            activeStep,
            isLower === 0 ? i : -i,
            false,
            isSpacing,
          );

          /************************************************************ */
          label.name = jawNamekey + "_ipr";
          label.visible = options.showLower;
          line.visible = options.showLower;
          line.name = jawNamekey + "_line";
          // i > 0 && label.scale.set(label.scale.x * 0.85, label.scale.y * 0.85, label.scale.z * 0.85)

          if (totalAmount || amount > 0) {
            line.add(label);
          }
          // if (startRevision && i > 0) {
          //   return;
          // }
          // teethMeshObject[jawNamekey+"_"++"_"+].add(label);

          // if ((totalAmount, amount > 0))
          // console.log(jawNamekey + "_st" + step + "_t" + ipr.from);

          if (totalAmount || amount > 0) {
            teethMeshObject[
              jawNamekey + "_st" + step + "_t" + ipr.from
            ].children[2].add(line);
            allIPRs.push(line);
          }
        });
      }
    });
  }
};
const getIPRPos = (
  iprStartPoint,
  upOrDown = 1,
  amount = 12,
  gumOffset = 0,
  theAngle = 45,
) => {
  // theAngle value between 0-45
  const zero = new THREE.Vector3();
  let one = iprStartPoint.clone();
  let rayDir = new THREE.Vector3();
  one.setZ(one.z + gumOffset);
  let angle = THREE.MathUtils.clamp(
    (Math.atan2(zero.x, zero.z) - Math.atan2(one.x, one.z)) * (180 / Math.PI),
    -70,
    70,
  );
  let forX = angle / -90;
  let forZ = 0;
  if (angle > 0) {
    forZ = forX + 1;
  } else {
    forZ = -(forX - 1);
  }
  rayDir = new THREE.Vector3(
    (forX * theAngle) / 45,
    upOrDown,
    (forZ * theAngle) / 45,
  );
  let t = iprStartPoint.add(rayDir.multiplyScalar(amount));
  return t;
};

export function prepareIPRs3DControls(
  _lastStepArray,
  iprOverLays,
  isOriginal = false,
) {
  if (_lastStepArray) {
    lastStepArray = _lastStepArray;
  } else if (!lastStepArray) {
    return;
  }
  const startTime = Date.now();

  for (let isLower = 0; isLower < 2; isLower++) {
    for (
      let toothIndex = isLower;
      toothIndex < lastStepArray[isLower].children.length - (isLower ? 0 : 1);
      toothIndex++
    ) {
      updateIPRBetweenTeeth(
        lastStepArray[isLower].children[toothIndex],
        iprOverLays,
        isOriginal,
      );
    }
  }
}

export function toggleIPRs(state = false) {
  for (let index = 0; index < allIPRs.length; index++) {
    if (allIPRs[index].parent) {
      allIPRs[index].parent.visible = state;
    } else {
      allIPRs.splice(allIPRs.indexOf(allIPRs[index]), 1);
      index--;
    }
  }
}
//#endregion
